import type { ReactNode } from "react";

import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  type StackProps,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  CheckNotchedIcon,
  LockIcon,
  SuitcaseOutlinedIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useFreelancerProfile } from "@js/apps/freelancer/hooks";
import {
  openIdentityVerificationModal,
  useUserNeedsIdentityVerification,
} from "@js/apps/identity-verification";
import { DefaultActionBar } from "@js/apps/jobs/components/job-details-action-bar/default-action-bar";
import { useJobActionBarContext } from "@js/apps/jobs/components/job-details-action-bar/job-context";

import { useGetFreelancerBidsPaginatedQuery } from "../../api";
import { useIsScreeningAvailable } from "../../hooks/use-is-screening-available";
import { PendingFreelancerActionButton } from "../pending-freelancer-action-button";

import styles from "./styles.module.scss";

type FreelancerActionBarProps = Pick<StackProps, "sx">;

export const FreelancerActionBarForJobDetailsPage = () => {
  const { job } = useJobActionBarContext();
  if (
    job?.status_for_freelancer !== ENUMS.JobFreelancerStatus.PENDING_APPROVAL &&
    job?.status_for_freelancer !==
      ENUMS.JobFreelancerStatus.PENDING_VERIFICATION
  ) {
    return null;
  }

  return <FreelancerActionBar sx={{ mb: { xs: 3, sm: 4.5, md: 6 } }} />;
};

export const FreelancerActionBar = (props: FreelancerActionBarProps) => {
  const { isScreeningAvailable, isScreeningRetake } = useIsScreeningAvailable();
  const { userNeedsIdentityVerification } = useUserNeedsIdentityVerification();

  if (userNeedsIdentityVerification) {
    return <FreelancerIdentityVerificationActionBar {...props} />;
  }

  if (isScreeningAvailable) {
    return (
      <FreelancerScreeningActionBar
        {...props}
        isScreeningRetake={isScreeningRetake}
      />
    );
  }

  return null;
};

const FreelancerScreeningActionBar = ({
  isScreeningRetake,
  ...props
}: FreelancerActionBarProps & { isScreeningRetake?: boolean }) => {
  const freelancer = useFreelancerProfile();

  const title = !freelancer?.created_via_public_application_flow
    ? "Congrats on your first application 🎉"
    : undefined;

  return (
    <DefaultActionBar
      title={title}
      subtitle={
        isScreeningRetake
          ? "Retake your video screening"
          : "Start your video screening!"
      }
      sx={{
        bgcolor: "var(--medium-green)",
      }}
      textColor="primary"
      titleProps={{
        variant: "label",
        size: "medium",
      }}
      subtitleProps={{
        variant: "title",
        size: "small",
        sx: { mb: 0.5 },
      }}
      content={
        <Typography
          color="primary"
          variant="paragraph"
          size="medium"
          component="p"
          fontWeight={400}
        >
          {isScreeningRetake ? (
            <>
              Not fully happy with your screening? You can retake it anytime —
              it only takes a few minutes and could strengthen your application.
            </>
          ) : (
            <>
              You've submitted your first application - nicely done. To be
              considered by the client, complete a quick video screening. It
              usually takes just a few minutes and unlocks the rest of
              Braintrust.
            </>
          )}
        </Typography>
      }
      actions={[
        <PendingFreelancerActionButton
          key="pending-action-button"
          variant="primary"
          withIcon
        />,
      ]}
      {...props}
    />
  );
};

const FreelancerIdentityVerificationActionBar = (
  props: FreelancerActionBarProps,
) => {
  const { data: mostRecentBids, isLoading } =
    useGetFreelancerBidsPaginatedQuery({
      ordering: "-created",
      page_size: 3, // fetch 3 as it's cached by the "I've applied to" section
      current: true,
      not_rejected: true,
    });

  const recentBid = mostRecentBids?.results[0];
  const recentBidText = recentBid
    ? `${recentBid.job.employer.name} · ${recentBid.job.title}`
    : undefined;

  const onVerifyIdentityClick = () => {
    openIdentityVerificationModal();
  };

  return (
    <Stack
      className={styles.verificationSection}
      sx={{
        borderRadius: 6,
        width: "100%",
        flexDirection: { xs: "column", lg: "row" },
        justifyContent: { md: "space-between" },
        gap: 3,
        background: "linear-gradient(108.12deg, #DEF2B1 0%, #D2EA9F 100%);",
        p: {
          xs: 3,
          sm: "40px",
        },
        ...props?.sx,
      }}
    >
      <Stack sx={{ flex: { lg: 1 }, maxWidth: "525px" }}>
        <Chip
          sx={{
            mb: 1.5,
            px: "12px !important",
            alignSelf: "start",
            backgroundColor: "rgba(93, 25, 116, 0.1) !important",
          }}
          size="x-small"
          label={
            <Typography
              fontWeight={500}
              component="label"
              className={styles.dotPrefix}
              sx={{
                fontSize: "12px !important",
                letterSpacing: "1px !important",
                color: "var(--dark-violet)",
              }}
            >
              ACTION REQUIRED
            </Typography>
          }
        />
        <Typography
          fontWeight={500}
          size="medium"
          component="h2"
          variant="title"
          sx={{ mb: 1.5, fontSize: "40px !important" }}
        >
          Verify your identity to finish applying.
        </Typography>
        <Typography size="medium" component="p" sx={{ mb: 3 }}>
          You've submitted your first application - nicely done. To be
          considered by the client, complete a quick identity check. It usually
          takes under 30 seconds and unlocks the rest of Braintrust.
        </Typography>
        <Stack direction="row" sx={{ alignItems: "center", gap: 2.5 }}>
          <Button
            variant="dark-violet"
            size="medium"
            onClick={onVerifyIdentityClick}
            sx={{
              height: { lg: "52px" },
              boxShadow: "0px 4px 12px 0px #5D197440 !important",
              minWidth: "228px !important",
            }}
            shape="squared"
          >
            Verify Identity
          </Button>
          <Typography size="small" component="p">
            Takes ~30 sec · Required once
          </Typography>
        </Stack>
      </Stack>
      <Stack
        sx={{
          alignSelf: "start",
          mt: { lg: "40px" },
          width: { xs: "100%", lg: "423px" },
          border: "1px solid var(--soft-violet)",
          p: 2.5,
          borderRadius: 4,
          background: "rgba(255, 255, 255, 0.65)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Typography
          component="p"
          size="small"
          fontWeight={500}
          sx={{
            fontSize: "12px !important",
            letterSpacing: "1px !important",
            mb: 2,
          }}
          color="grey-2"
        >
          <LockIcon
            sx={{
              color: "inherit",
              fontSize: "11px",
              verticalAlign: "text-top",
              mr: 1,
            }}
          />
          UNLOCKS WHEN VERIFIED
        </Typography>
        <Stack component="ul" sx={{ m: 0, p: 0 }}>
          <UnlockItem
            icon={<CheckNotchedIcon sx={{ fontSize: "16px" }} />}
            title="Your application"
            subtitle={isLoading ? "..." : recentBidText}
            chip={
              <Chip
                size="x-small"
                sx={{
                  background: "rgba(170, 105, 8, 0.12) !important",
                  borderRadius: "6px !important",
                  py: "2px !important",
                }}
                LabelProps={{ sx: { lineHeight: "1 !important" } }}
                label={
                  <Typography
                    component="label"
                    fontWeight={500}
                    sx={{
                      fontSize: "11px !important",
                      letterSpacing: "1px !important",
                      color: "var(--dark-yellow)",
                    }}
                  >
                    PENDING
                  </Typography>
                }
              />
            }
          />

          <Divider color="beige" sx={{ my: 1.5 }} />
          <UnlockItem
            icon={<SuitcaseOutlinedIcon sx={{ fontSize: "16px" }} />}
            title="Apply to more jobs"
            subtitle="Keep your job search moving forward"
            chip={
              <Chip
                size="x-small"
                sx={{
                  background: "rgba(93, 25, 116, 0.1) !important",
                  borderRadius: "6px !important",
                  py: "2px !important",
                }}
                LabelProps={{ sx: { lineHeight: "1 !important" } }}
                label={
                  <Typography
                    component="label"
                    fontWeight={500}
                    sx={{
                      fontSize: "11px !important",
                      letterSpacing: "1px !important",
                      color: "var(--dark-violet)",
                    }}
                  >
                    LOCKED
                  </Typography>
                }
              />
            }
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

type UnlockItemProps = {
  icon: ReactNode;
  title: ReactNode;
  subtitle: ReactNode;
  chip: ReactNode;
};
const UnlockItem = ({ icon, title, subtitle, chip }: UnlockItemProps) => {
  return (
    <Stack
      component="li"
      direction="row"
      sx={{ gap: 1.5, width: "100%", overflow: "hidden", alignItems: "center" }}
    >
      <Box
        sx={{
          width: "36px",
          height: "36px",
          border: "1px solid rgba(20, 20, 20, 0.06)",
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          background: "var(--white)",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Stack sx={{ overflow: "hidden" }}>
        <Typography
          component="h4"
          size="small"
          fontWeight={500}
          sx={{ fontSize: "14px !important", lineHeight: "18px !important" }}
        >
          {title}
        </Typography>
        {!!subtitle && (
          <Typography
            component="label"
            size="small"
            color="grey-2"
            sx={{ lineHeight: "18px !important", fontSize: "12px !important" }}
            ellipsis
          >
            {subtitle}
          </Typography>
        )}
      </Stack>
      <Box sx={{ ml: "auto", flexShrink: 0 }}>{chip}</Box>
    </Stack>
  );
};
